// Import the Firebase modules we need
import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getAnalytics, isSupported, Analytics } from 'firebase/analytics';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getStorage, FirebaseStorage } from 'firebase/storage';

// Clean any whitespace from environment variables
const cleanEnvValue = (value: string | undefined): string => {
  if (!value) return '';
  // Remove any whitespace, newlines, etc.
  return value.trim();
};

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: cleanEnvValue(import.meta.env.VITE_FIREBASE_API_KEY),
  authDomain: cleanEnvValue(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN),
  projectId: cleanEnvValue(import.meta.env.VITE_FIREBASE_PROJECT_ID),
  storageBucket: cleanEnvValue(import.meta.env.VITE_FIREBASE_STORAGE_BUCKET),
  messagingSenderId: cleanEnvValue(import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID),
  appId: cleanEnvValue(import.meta.env.VITE_FIREBASE_APP_ID),
  measurementId: cleanEnvValue(import.meta.env.VITE_FIREBASE_MEASUREMENT_ID)
};

// Validate Firebase configuration
const validateFirebaseConfig = () => {
  const requiredKeys = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];
  const missingKeys = requiredKeys.filter(key => !firebaseConfig[key as keyof typeof firebaseConfig]);
  
  if (missingKeys.length > 0) {
    console.error(`Firebase initialization error: Missing required config values: ${missingKeys.join(', ')}`);
    console.error('Current config:', JSON.stringify({ 
      apiKey: firebaseConfig.apiKey ? '****' : null,
      authDomain: firebaseConfig.authDomain,
      projectId: firebaseConfig.projectId,
      storageBucket: firebaseConfig.storageBucket ? '****' : null,
      messagingSenderId: firebaseConfig.messagingSenderId ? '****' : null,
      appId: firebaseConfig.appId ? '****' : null,
      measurementId: firebaseConfig.measurementId ? '****' : null
    }, null, 2));
    return false;
  }
  return true;
};

// Only initialize if config is valid
let app: FirebaseApp | undefined;
let auth: Auth | undefined;
let analytics: Analytics | null = null;
let db: Firestore | undefined;
let storage: FirebaseStorage | undefined;

try {
  if (validateFirebaseConfig()) {
    console.log('Firebase configuration validated successfully');
    // Initialize Firebase
    app = initializeApp(firebaseConfig);

    // Initialize Firebase Authentication
    auth = getAuth(app);
    auth.useDeviceLanguage(); // Use the device's language
    
    // Set persistence to LOCAL (this is actually the default, but we're making it explicit)
    setPersistence(auth, browserLocalPersistence).catch((error) => {
      console.error("Error setting persistence:", error);
    });

    // Initialize Firestore
    db = getFirestore(app);
    
    // Initialize Storage
    storage = getStorage(app);

    // Check Firestore connection on initialization
    if (db) {
      const timestamp = new Date().toISOString();
      console.log(`Firebase initialized at ${timestamp}`);
    } else {
      console.error('Failed to initialize Firestore connection');
    }

    // Initialize Analytics only if supported
    if (typeof window !== 'undefined') {
      isSupported().then(supported => {
        if (supported && app) {
          analytics = getAnalytics(app);
        }
      }).catch(error => {
        console.error("Firebase Analytics error:", error);
      });
    }
  } else {
    console.error('Firebase configuration validation failed. Firebase services will not be available.');
  }
} catch (error) {
  console.error('Error initializing Firebase:', error);
}

export { auth, analytics, db, storage };
export default app; 